import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import InlineSelect from '../ui/InlineSelect'
import MultiSelect from '../ui/MultiSelect'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { CatalogCategoryKey } from '../../catalog/categoryKeys'
import {
  expandPassSequenceToLabels,
  legacyWeldDetailsGroupKey,
  passSectionInputForBevel,
  weldDetailsGroupStorageKey
} from '../../utils/passSequenceExpanded'
import { composeMinMaxFieldValues } from '../../utils/pqrWeldingVariableRowDefs'
import {
  copyIwmRootToRcFields,
  isRcFieldsEffectivelyEmpty,
  isRootPassFamily,
  ROOT_IWM_PASS_LABEL,
  ROOT_RC_PASS_LABEL,
  weldDetailsPassGroupsFromExpanded,
  weldDetailsPassTabLabel,
  weldDetailsRootSubTabLabel
} from '../../utils/weldDetailsRootRcCopy'

type WeldDetailState = {
  fields: Record<string, string>
  units: Record<string, string>
}

type WeldDetailStore = {
  template?: WeldDetailState
  groups?: Record<string, WeldDetailState>
}

type FieldDef = {
  key: string
  label: string
  unitType?: 'length'
  hint?: string
}

const sections: Array<{ title: string; fields: FieldDef[] }> = [
  {
    title: 'Process',
    fields: [
      { key: 'weldingProcess', label: 'Welding Process' },
      { key: 'weldingPosition', label: 'Welding Position' },
      { key: 'weldingDirection', label: 'Welding Direction' },
      { key: 'weldingSystem', label: 'Welding System' }
    ]
  },
  {
    title: 'Torch & Setup',
    fields: [
      { key: 'torchConfiguration', label: 'Torch Configuration' },
      { key: 'torchSplitDistance', label: 'Torch Split Distance', unitType: 'length' }
    ]
  },
  {
    title: 'Filler Metal',
    fields: [
      { key: 'fillerManufacturer', label: 'Filler Metal Manufacturer' },
      { key: 'fillerTradeName', label: 'Filler Metal Trade Name' },
      { key: 'fillerAwsSpec', label: 'Filler Metal AWS Specification' },
      { key: 'fillerAwsClass', label: 'Filler Metal AWS Classification' },
      { key: 'fillerHeatNo', label: 'Filler Metal Batch/Heat No.' },
      { key: 'wireDiameter', label: 'El./Wire Diameter', unitType: 'length' }
    ]
  },
  {
    title: 'Shielding & Gas',
    fields: [
      { key: 'shieldingComposition', label: 'Shielding Composition (%)' },
      { key: 'awsDesignator', label: 'AWS Designator' },
      { key: 'gasFlowRateMin', label: 'Gas Flow Rate Min (cfh)' },
      { key: 'gasFlowRateMax', label: 'Gas Flow Rate Max (cfh)' },
      { key: 'gasNozzleOrifice', label: 'Gas Nozzle Pup Size', unitType: 'length' }
    ]
  },
  {
    title: 'Technique',
    fields: [
      { key: 'ctwd', label: 'C.T.W.D', unitType: 'length' },
      { key: 'stringOrWeave', label: 'String or Weave' },
      { key: 'oscillationRateMin', label: 'Oscillation Rate Min (BPM)' },
      { key: 'oscillationRateMax', label: 'Oscillation Rate Max (BPM)' },
      { key: 'oscillationWidthMin', label: 'Oscillation Width Min' },
      { key: 'oscillationWidthMax', label: 'Oscillation Width Max' },
      { key: 'dwellTimeMin', label: 'Dwell Time Min (s)' },
      { key: 'dwellTimeMax', label: 'Dwell Time Max (s)' },
      { key: 'heatAngle', label: 'Head Angle (°)' }
    ]
  },
  {
    title: 'Electrical',
    fields: [{ key: 'electricalPolarity', label: 'Electrical Current/Polarity' }]
  }
]

/** Maps weld detail field keys to global catalog category_key (Settings → Dropdown Catalog). */
const WELD_FIELD_CATALOG: Record<string, CatalogCategoryKey> = {
  weldingProcess: 'weld_welding_process',
  weldingPosition: 'weld_welding_position',
  weldingDirection: 'weld_welding_direction',
  weldingSystem: 'weld_welding_system',
  torchConfiguration: 'weld_torch_configuration',
  torchSplitDistance: 'weld_torch_split_distance',
  fillerManufacturer: 'weld_filler_manufacturer',
  fillerTradeName: 'weld_filler_trade_name',
  fillerAwsSpec: 'weld_filler_aws_spec',
  fillerAwsClass: 'weld_filler_aws_class',
  fillerHeatNo: 'weld_filler_heat_no',
  wireDiameter: 'weld_wire_diameter',
  shieldingComposition: 'weld_shielding_composition',
  awsDesignator: 'weld_aws_designator',
  ctwd: 'weld_ctwd',
  stringOrWeave: 'weld_string_or_weave',
  heatAngle: 'weld_heat_angle',
  electricalPolarity: 'weld_electrical_polarity'
}

type ComposedRangeUi = {
  minKey: string
  maxKey: string
  rowKey: string
  label: string
  inputType: 'number' | 'catalog'
  catalogKey?: CatalogCategoryKey
  unitKey?: string
  unitOptions?: Array<{ value: string; label: string }>
}

const isNaCatalogValue = (value: string) => /^n\/a$/i.test(value.trim())

function patchComposedRangeFields(
  fields: Record<string, string>,
  config: ComposedRangeUi,
  side: 'min' | 'max',
  next: string
): Record<string, string> {
  const updated = { ...fields }
  const fieldKey = side === 'min' ? config.minKey : config.maxKey
  updated[fieldKey] = next
  if (side === 'min') {
    if (isNaCatalogValue(next)) {
      updated[config.maxKey] = 'N/A'
    } else if (isNaCatalogValue(updated[config.maxKey] ?? '')) {
      updated[config.maxKey] = ''
    }
  }
  return updated
}

function ComposedRangeMaxClearButton({
  label,
  visible,
  onClear
}: {
  label: string
  visible: boolean
  onClear: () => void
}) {
  if (!visible) return null
  return (
    <button
      type="button"
      className="shrink-0 rounded-md border border-gray-300 px-2 py-1.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      aria-label={`Clear ${label} max`}
      onClick={onClear}
    >
      Clear
    </button>
  )
}

function normalizeNaComposedRanges(fields: Record<string, string>): void {
  for (const config of Object.values(COMPOSED_RANGE_UI)) {
    if (config.inputType !== 'catalog') continue
    if (isNaCatalogValue(fields[config.minKey] ?? '') && !String(fields[config.maxKey] ?? '').trim()) {
      fields[config.maxKey] = 'N/A'
    }
  }
}

/** Min/max field pairs rendered as a single row (mirrors Gas Flow Rate UX). */
const COMPOSED_RANGE_UI: Record<string, ComposedRangeUi> = {
  gasFlowRateMin: {
    minKey: 'gasFlowRateMin',
    maxKey: 'gasFlowRateMax',
    rowKey: 'gasFlowRate',
    label: 'Gas Flow Rate (cfh)',
    inputType: 'number',
    unitKey: 'gasFlowRate',
    unitOptions: [
      { value: 'cfh', label: 'cfh' },
      { value: 'L/min', label: 'L/min' }
    ]
  },
  oscillationRateMin: {
    minKey: 'oscillationRateMin',
    maxKey: 'oscillationRateMax',
    rowKey: 'oscillationRate',
    label: 'Oscillation Rate',
    inputType: 'catalog',
    catalogKey: 'weld_oscillation_rate',
    unitKey: 'oscillationRate',
    unitOptions: [{ value: 'BPM', label: 'BPM' }]
  },
  oscillationWidthMin: {
    minKey: 'oscillationWidthMin',
    maxKey: 'oscillationWidthMax',
    rowKey: 'oscillationWidth',
    label: 'Oscillation Width',
    inputType: 'catalog',
    catalogKey: 'weld_oscillation_width',
    unitKey: 'oscillationWidth',
    unitOptions: [
      { value: 'in', label: 'in' },
      { value: 'mm', label: 'mm' }
    ]
  },
  dwellTimeMin: {
    minKey: 'dwellTimeMin',
    maxKey: 'dwellTimeMax',
    rowKey: 'dwellTime',
    label: 'Dwell Time',
    inputType: 'catalog',
    catalogKey: 'weld_dwell_time',
    unitKey: 'dwellTime',
    unitOptions: [{ value: 's', label: 's' }]
  }
}

const HIDDEN_COMPOSED_MAX_KEYS = new Set(
  Object.values(COMPOSED_RANGE_UI).map((config) => config.maxKey)
)

const LEGACY_RANGE_FIELDS: Array<{ legacy: string; min: string; max: string }> = [
  { legacy: 'gasFlowRate', min: 'gasFlowRateMin', max: 'gasFlowRateMax' },
  { legacy: 'oscillationRate', min: 'oscillationRateMin', max: 'oscillationRateMax' },
  { legacy: 'oscillationWidth', min: 'oscillationWidthMin', max: 'oscillationWidthMax' },
  { legacy: 'dwellTime', min: 'dwellTimeMin', max: 'dwellTimeMax' }
]

const fieldOptions: Record<string, string[]> = {
  weldingProcess: ['GMAW-S', 'GMAW-P', 'FCAW', 'SMAW'],
  weldingPosition: ['Fixed', '5G', '6G'],
  weldingDirection: ['Downhill', 'Uphill'],
  weldingSystem: ['IWM / Automatic', 'P600 / Mechanized', 'Manual'],
  torchConfiguration: ['Single', 'Dual', '3 / Side'],
  torchSplitDistance: [],
  fillerManufacturer: ['Lincoln Electric'],
  fillerTradeName: ['CRC-70S', 'CRC-80Ni1'],
  fillerAwsSpec: ['A5.18', 'A5.28'],
  fillerAwsClass: ['ER70S-6', 'ER80S-G'],
  fillerHeatNo: [],
  wireDiameter: ['0.035', '0.040'],
  shieldingComposition: ['Argon/CO2 80% / 20%'],
  awsDesignator: ['M21-ArC-20'],
  gasFlowRateMin: [],
  gasFlowRateMax: [],
  gasNozzleOrifice: [],
  ctwd: ['Automatic'],
  stringOrWeave: ['Stringer', 'Weave / Stringer', 'Weave'],
  oscillationRateMin: ['N/A'],
  oscillationRateMax: ['N/A'],
  oscillationWidthMin: ['N/A'],
  oscillationWidthMax: ['N/A'],
  dwellTimeMin: ['N/A'],
  dwellTimeMax: ['N/A'],
  heatAngle: ['+5° leading', '+6° leading'],
  electricalPolarity: ['DCEP', 'DCEN']
}

const buildDefaultState = (): WeldDetailState => {
  const fields = sections.reduce((acc, section) => {
    section.fields.forEach((field) => {
      acc[field.key] = ''
    })
    return acc
  }, {} as Record<string, string>)
  const units = sections.reduce((acc, section) => {
    section.fields.forEach((field) => {
      if (field.unitType === 'length') {
        acc[field.key] = 'in'
      }
    })
    return acc
  }, {} as Record<string, string>)
  units.gasFlowRate = 'cfh'
  units.oscillationRate = 'BPM'
  units.oscillationWidth = 'in'
  units.dwellTime = 's'
  return { fields, units }
}

function migrateLegacyRangeFields(fields: Record<string, string>): void {
  for (const { legacy, min, max } of LEGACY_RANGE_FIELDS) {
    if (!(legacy in fields)) continue
    const hasMinMax =
      String(fields[min] ?? '').trim() !== '' || String(fields[max] ?? '').trim() !== ''
    if (!hasMinMax) {
      const split = composeMinMaxFieldValues(fields, min, max, legacy)
      fields[min] = split.min
      fields[max] = split.max
    }
    delete fields[legacy]
  }
}

/** Normalize persisted weld detail JSON (legacy keys, removed fields). */
function migrateWeldDetailState(raw: WeldDetailState | null | undefined): WeldDetailState {
  const base = buildDefaultState()
  if (!raw) return base
  const fields = { ...base.fields, ...raw.fields } as Record<string, string>
  const units = { ...base.units, ...raw.units } as Record<string, string>
  migrateLegacyRangeFields(fields)
  normalizeNaComposedRanges(fields)
  if (!units.gasFlowRate) units.gasFlowRate = 'cfh'
  if (!units.oscillationRate) units.oscillationRate = 'BPM'
  if (!units.oscillationWidth) units.oscillationWidth = 'in'
  if (!units.dwellTime) units.dwellTime = 's'
  return { fields, units }
}

function sanitizeWeldDetailState(s: WeldDetailState): WeldDetailState {
  const base = buildDefaultState()
  const fields = { ...base.fields }
  const units = { ...base.units }
  Object.keys(base.fields).forEach((k) => {
    if (k in s.fields) fields[k] = s.fields[k]
  })
  Object.keys(base.units).forEach((k) => {
    if (k in s.units) units[k] = s.units[k]
  })
  return { fields, units }
}

function readWeldGroupState(
  groups: Record<string, WeldDetailState> | undefined,
  bevelIndex: number,
  passLabel: string
): WeldDetailState | null {
  if (!groups) return null
  const composite = weldDetailsGroupStorageKey(bevelIndex, passLabel)
  if (groups[composite]) return groups[composite]
  if (/^Root Misfire( \d+)?$/.test(passLabel) || passLabel === ROOT_RC_PASS_LABEL) {
    const candidates =
      passLabel === ROOT_RC_PASS_LABEL
        ? [ROOT_RC_PASS_LABEL, 'Root Misfire 1', 'Root Misfire', 'Root Misfire 2']
        : passLabel === 'Root Misfire'
          ? ['Root Misfire 1', 'Root Misfire', ROOT_RC_PASS_LABEL]
          : [passLabel, 'Root Misfire', 'Root Misfire 1', ROOT_RC_PASS_LABEL]
    for (const altLabel of candidates) {
      const altKey = weldDetailsGroupStorageKey(bevelIndex, altLabel)
      if (groups[altKey]) return groups[altKey]
    }
  }
  if (bevelIndex === 0) {
    if (groups[passLabel]) return groups[passLabel]
    const legacy = legacyWeldDetailsGroupKey(passLabel)
    if (legacy && groups[legacy]) return groups[legacy]
  }
  return null
}

export default function WpsWeldDetails() {
  const { id, pipeId, passKey, bevelIndex: bevelIndexParam } = useParams()
  const navigate = useNavigate()
  const { getProject } = useProjects()
  const { getSection, updateSection, sections: wpsSections, loading: wpsSectionsLoading } =
    useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [passGroup, setPassGroup] = useState(passKey ? decodeURIComponent(passKey) : 'Root')
  const [state, setState] = useState<WeldDetailState>(buildDefaultState())
  const [showCopyPanel, setShowCopyPanel] = useState(false)
  const [copyTargets, setCopyTargets] = useState<string[]>([])
  const [copySections, setCopySections] = useState<string[]>(
    sections.map((section) => section.title)
  )
  const openRangePickerCountRef = useRef(0)
  const [elevatedRangeRowKey, setElevatedRangeRowKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleRangePickerOpenChange = (rowKey: string, isOpen: boolean) => {
    if (isOpen) {
      openRangePickerCountRef.current += 1
      setElevatedRangeRowKey(rowKey)
      return
    }
    openRangePickerCountRef.current = Math.max(0, openRangePickerCountRef.current - 1)
    if (openRangePickerCountRef.current === 0) {
      setElevatedRangeRowKey(null)
    }
  }

  const sectionFieldMap = useMemo(() => {
    const acc = sections.reduce((map, section) => {
      map[section.title] = section.fields.map((field) => field.key)
      return map
    }, {} as Record<string, string[]>)
    const shield = acc['Shielding & Gas']
    if (shield && !shield.includes('gasFlowRate')) {
      acc['Shielding & Gas'] = [...shield, 'gasFlowRate']
    }
    return acc
  }, [])

  const urlBevelRaw =
    bevelIndexParam !== undefined && bevelIndexParam !== ''
      ? Number(bevelIndexParam)
      : 0
  const urlBevelParsed = Number.isFinite(urlBevelRaw) ? Math.trunc(urlBevelRaw) : 0

  const passSnap = wpsSections.pass
  const bevelPassCount = useMemo(() => {
    if (passSnap && typeof passSnap === 'object' && Array.isArray(passSnap.bevelPasses)) {
      return Math.max(1, passSnap.bevelPasses.length)
    }
    return 1
  }, [passSnap])

  const clampedBevelIndex = useMemo(() => {
    if (wpsSectionsLoading) {
      return Math.max(0, urlBevelParsed)
    }
    return Math.min(bevelPassCount - 1, Math.max(0, urlBevelParsed))
  }, [wpsSectionsLoading, bevelPassCount, urlBevelParsed])

  const passGroups = useMemo(() => {
    if (!pipeId) return []
    return weldDetailsPassGroupsFromExpanded(
      expandPassSequenceToLabels(passSectionInputForBevel(passSnap, clampedBevelIndex))
    )
  }, [pipeId, passSnap, clampedBevelIndex])

  useEffect(() => {
    if (passKey) setPassGroup(decodeURIComponent(passKey))
  }, [passKey])

  useEffect(() => {
    if (wpsSectionsLoading) return
    if (!pipeId || !id || bevelIndexParam === undefined || bevelIndexParam === '') return
    if (urlBevelParsed === clampedBevelIndex) return
    navigate(
      `/projects/${id}/next/${pipeId}/pass/bevel/${clampedBevelIndex}/${encodeURIComponent(passGroup)}`,
      { replace: true }
    )
  }, [
    wpsSectionsLoading,
    pipeId,
    id,
    bevelIndexParam,
    urlBevelParsed,
    clampedBevelIndex,
    passGroup,
    navigate
  ])

  useEffect(() => {
    if (wpsSectionsLoading) return
    if (!pipeId || !id || passGroups.length === 0) return
    const passGroupAllowed =
      passGroups.includes(passGroup) ||
      (passGroup === ROOT_RC_PASS_LABEL && passGroups.includes(ROOT_IWM_PASS_LABEL))
    if (!passGroupAllowed) {
      const next = passGroups[0]
      setPassGroup(next)
      const pathBevel =
        bevelIndexParam !== undefined && bevelIndexParam !== ''
          ? clampedBevelIndex
          : 0
      const useBevelPath = bevelPassCount > 1 || bevelIndexParam !== undefined
      const to = useBevelPath
        ? `/projects/${id}/next/${pipeId}/pass/bevel/${pathBevel}/${encodeURIComponent(next)}`
        : `/projects/${id}/next/${pipeId}/pass/${encodeURIComponent(next)}`
      navigate(to, { replace: true })
    }
  }, [
    wpsSectionsLoading,
    pipeId,
    id,
    passGroups,
    passGroup,
    navigate,
    clampedBevelIndex,
    bevelPassCount,
    bevelIndexParam
  ])

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('weldDetails') as WeldDetailStore | null
    if (parsed) {
      try {
        const groupState = readWeldGroupState(parsed.groups, clampedBevelIndex, passGroup)
        if (groupState) {
          let next = migrateWeldDetailState(groupState)
          if (passGroup === ROOT_RC_PASS_LABEL && isRcFieldsEffectivelyEmpty(next.fields)) {
            const iwm = readWeldGroupState(parsed.groups, clampedBevelIndex, 'Root')
            if (iwm) {
              const iwmState = migrateWeldDetailState(iwm)
              next = {
                ...next,
                fields: copyIwmRootToRcFields(iwmState.fields, next.fields),
                units: { ...iwmState.units, ...next.units }
              }
            }
          }
          setState(next)
          return
        }
        if (passGroup === ROOT_RC_PASS_LABEL) {
          const iwm = readWeldGroupState(parsed.groups, clampedBevelIndex, 'Root')
          if (iwm) {
            const iwmState = migrateWeldDetailState(iwm)
            setState({
              fields: copyIwmRootToRcFields(iwmState.fields, {}),
              units: { ...iwmState.units }
            })
            return
          }
        }
        if (parsed.template) {
          setState(migrateWeldDetailState(parsed.template))
          return
        }
      } catch (error) {
        // ignore
      }
    }
    setState(buildDefaultState())
  }, [pipeId, passGroup, clampedBevelIndex, getSection])

  const weldPathForPass = (passLabel: string) => {
    if (!project || !pipeId) return '#'
    const useBevelPath = bevelPassCount > 1 || bevelIndexParam !== undefined
    return useBevelPath
      ? `/projects/${project.id}/next/${pipeId}/pass/bevel/${clampedBevelIndex}/${encodeURIComponent(passLabel)}`
      : `/projects/${project.id}/next/${pipeId}/pass/${encodeURIComponent(passLabel)}`
  }

  const handleSave = async () => {
    if (!pipeId || saving) return
    setSaving(true)
    try {
      const existing = (getSection('weldDetails') as WeldDetailStore | null) || {}
      const nextState = sanitizeWeldDetailState(state)
      const storageKey = weldDetailsGroupStorageKey(clampedBevelIndex, passGroup)
      const groups = { ...(existing.groups || {}), [storageKey]: nextState }
      const template = sanitizeWeldDetailState(migrateWeldDetailState(existing.template ?? nextState))
      await updateSection('weldDetails', { template, groups })
      toast.success('Weld details saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save weld details')
    } finally {
      setSaving(false)
    }
  }

  const handleApplyCopy = async () => {
    if (!pipeId || copyTargets.length === 0 || saving) return
    const existing = (getSection('weldDetails') as WeldDetailStore | null) || {}
    const activeFields = new Set(
      copySections.flatMap((sectionTitle) => sectionFieldMap[sectionTitle] || [])
    )
    const baseTemplate = migrateWeldDetailState(existing.template || buildDefaultState())
    const nextGroups = { ...(existing.groups || {}) }

    copyTargets.forEach((targetPassLabel) => {
      const targetKey = weldDetailsGroupStorageKey(clampedBevelIndex, targetPassLabel)
      const targetState = migrateWeldDetailState(
        readWeldGroupState(nextGroups, clampedBevelIndex, targetPassLabel) || baseTemplate
      )
      const merged: WeldDetailState = {
        fields: { ...targetState.fields },
        units: { ...targetState.units }
      }
      Object.keys(state.fields).forEach((key) => {
        if (!activeFields.has(key)) return
        merged.fields[key] = state.fields[key]
      })
      Object.keys(state.units).forEach((key) => {
        if (!activeFields.has(key)) return
        merged.units[key] = state.units[key]
      })
      nextGroups[targetKey] = sanitizeWeldDetailState(merged)
    })

    setSaving(true)
    try {
      await updateSection('weldDetails', {
        template: sanitizeWeldDetailState(baseTemplate),
        groups: nextGroups
      })
      setShowCopyPanel(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not copy weld details')
    } finally {
      setSaving(false)
    }
  }

  // Slice 07 of pqr-preview-field-navigation: the PQR Preview routes
  // each Welding Variables cell click here as
  // `?focusField=<rowKey>&returnTo=…`. Locate the matching input via
  // `data-focus-field`, scroll it into view, focus it, and flash it.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const formContainerRef = useRef<HTMLDivElement>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const root = formContainerRef.current ?? document
      const el = root.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      if (!el) return null
      // Prefer an inner focusable control (input, select, button) when
      // the wrapper isn't itself focusable. Falls back to the wrapper.
      const inner = el.querySelector(
        'input, select, textarea, button, [role="button"]'
      ) as HTMLElement | null
      return inner ?? el
    }
  })
  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : id && pipeId
      ? {
          onClick: () =>
            navigate(`/projects/${id}/next/${pipeId}/pass`, { replace: true }),
          label: 'Back'
        }
      : { onClick: () => navigate(-1), label: 'Back' }

  if (!project || !pipeSet) {
    return (
      <Card title="Weld Details not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4" ref={formContainerRef}>
      <EditingReturnBanner returnTo={returnTo} contextLabel="Weld Details" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={<ProjectPageTitle label="Weld Details" projectName={project.name} />}
        subtitle={
          <>
            {formatClientLocationSubtitle(project) ? (
              <span>{formatClientLocationSubtitle(project)}</span>
            ) : null}
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
            {bevelPassCount > 1 && (
              <Badge tone="gray">Bevel {clampedBevelIndex + 1}</Badge>
            )}
            <Badge tone="gray">
              {isRootPassFamily(passGroup)
                ? `Root · ${weldDetailsRootSubTabLabel(passGroup)}`
                : weldDetailsPassTabLabel(passGroup)}
            </Badge>
          </>
        }
        actions={
          <>
            <Button loading={saving} disabled={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      {passGroups.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {passGroups.map((group) => {
                  const isActive =
                    group === passGroup ||
                    (group === ROOT_IWM_PASS_LABEL && isRootPassFamily(passGroup))
                  return (
                    <Link
                      key={group}
                      to={weldPathForPass(group)}
                      replace
                      className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition ${
                        isActive
                          ? 'border-primary-500 text-primary-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {weldDetailsPassTabLabel(group)}
                    </Link>
                  )
                })}
              </div>
              <button
                type="button"
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition hover:border-gray-300 hover:text-gray-800"
                onClick={() => setShowCopyPanel((prev) => !prev)}
              >
                Copy to…
              </button>
            </div>
          </div>
          {passGroups.includes(ROOT_IWM_PASS_LABEL) && isRootPassFamily(passGroup) ? (
            <div
              className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-2"
              data-testid="weld-details-root-subtabs"
            >
              {([ROOT_IWM_PASS_LABEL, ROOT_RC_PASS_LABEL] as const).map((sub) => (
                <Link
                  key={sub}
                  to={weldPathForPass(sub)}
                  replace
                  className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition ${
                    passGroup === sub
                      ? 'border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {weldDetailsRootSubTabLabel(sub)}
                </Link>
              ))}
            </div>
          ) : null}
          {showCopyPanel && (
            <div className="px-4 py-3 text-xs text-gray-600">
              <div className="grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold text-gray-500">Copy current pass to</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      ...passGroups.filter((group) => group !== passGroup),
                      ...(isRootPassFamily(passGroup)
                        ? [
                            passGroup === ROOT_IWM_PASS_LABEL
                              ? ROOT_RC_PASS_LABEL
                              : ROOT_IWM_PASS_LABEL
                          ]
                        : [])
                    ]
                      .filter((group, idx, arr) => arr.indexOf(group) === idx)
                      .map((group) => (
                        <label
                          key={`copy-${group}`}
                          className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600"
                        >
                          <input
                            type="checkbox"
                            checked={copyTargets.includes(group)}
                            onChange={(event) => {
                              setCopyTargets((prev) =>
                                event.target.checked
                                  ? [...prev, group]
                                  : prev.filter((item) => item !== group)
                              )
                            }}
                            className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                          />
                          <span>
                            {isRootPassFamily(group)
                              ? `Root · ${weldDetailsRootSubTabLabel(group)}`
                              : group}
                          </span>
                        </label>
                      ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-500">Sections to copy</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {sections.map((section) => (
                      <label
                        key={`section-${section.title}`}
                        className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600"
                      >
                        <input
                          type="checkbox"
                          checked={copySections.includes(section.title)}
                          onChange={(event) => {
                            setCopySections((prev) =>
                              event.target.checked
                                ? [...prev, section.title]
                                : prev.filter((item) => item !== section.title)
                            )
                          }}
                          className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                        />
                        <span>{section.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button variant="secondary" type="button" onClick={() => setShowCopyPanel(false)}>
                  Cancel
                </Button>
                <Button type="button" loading={saving} disabled={saving} onClick={() => void handleApplyCopy()}>
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 overflow-visible lg:grid-cols-2 lg:items-start">
        {sections.map((section) => {
          const sectionHasElevatedPicker =
            elevatedRangeRowKey != null &&
            section.fields.some((f) => COMPOSED_RANGE_UI[f.key]?.minKey === elevatedRangeRowKey)
          return (
          <div
            key={section.title}
            className={`overflow-visible rounded-lg border border-gray-200 bg-white p-3 ${
              sectionHasElevatedPicker ? 'relative z-[10000]' : ''
            }`}
          >
            <p className="text-xs font-semibold text-gray-700">{section.title}</p>
            <div className="mt-3 grid gap-3 overflow-visible md:grid-cols-2">
              {section.fields.map((field) => {
                if (HIDDEN_COMPOSED_MAX_KEYS.has(field.key) || field.key === 'gasFlowRateMax') {
                  return null
                }
                const composed = COMPOSED_RANGE_UI[field.key]
                const focusFieldKey = composed?.rowKey ?? field.key
                return (
                <div
                  key={field.key}
                  data-focus-field={focusFieldKey}
                  className={`relative flex flex-col gap-1 ${
                    section.fields.length === 1 ? 'md:col-span-2' : ''
                  } ${composed ? 'md:col-span-2' : ''} ${
                    composed && elevatedRangeRowKey === composed.minKey ? 'z-[9999]' : ''
                  }`}
                >
                  <label className="text-xs font-medium text-gray-500">
                    {(composed?.label ?? field.label)} *
                  </label>
                  <div className="flex items-center gap-2">
                    {field.key === 'torchSplitDistance' ? (
                      <div className="flex w-full flex-wrap items-center gap-2">
                        <MultiSelect
                          className="min-w-0 flex-1"
                          maxSelections={1}
                          values={state.fields.torchSplitDistance ? [state.fields.torchSplitDistance] : []}
                          options={getLabelsMerged(
                            WELD_FIELD_CATALOG.torchSplitDistance,
                            fieldOptions.torchSplitDistance ?? []
                          )}
                          placeholder="Select or add..."
                          addLabel="Add Value"
                          inputClassName="h-9 text-xs"
                          onChange={(values) => {
                            const next = values.length ? values[values.length - 1] : ''
                            setState((prev) => ({
                              ...prev,
                              fields: { ...prev.fields, torchSplitDistance: next }
                            }))
                          }}
                          onCreateOption={async (value) => {
                            await addLabel(WELD_FIELD_CATALOG.torchSplitDistance, value)
                            setState((prev) => ({
                              ...prev,
                              fields: { ...prev.fields, torchSplitDistance: value }
                            }))
                          }}
                        />
                        <InlineSelect
                          value={state.units[field.key] || 'in'}
                          options={[
                            { value: 'in', label: 'in' },
                            { value: 'mm', label: 'mm' }
                          ]}
                          onChange={(value) =>
                            setState((prev) => ({
                              ...prev,
                              units: { ...prev.units, [field.key]: value }
                            }))
                          }
                          className="w-16 shrink-0"
                          buttonClassName="h-9 px-2 text-xs justify-between"
                        />
                      </div>
                    ) : composed ? (
                      composed.inputType === 'catalog' && composed.catalogKey ? (
                        (() => {
                          const catalogOptions = getLabelsMerged(
                            composed.catalogKey,
                            fieldOptions[composed.minKey] ?? ['N/A']
                          )
                          const minValue = state.fields[composed.minKey] || ''
                          const maxValue = state.fields[composed.maxKey] || ''
                          const minIsNa = isNaCatalogValue(minValue)
                          return (
                            <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                              <MultiSelect
                                className="min-w-0 w-full"
                                maxSelections={1}
                                dropdownPortal
                                values={minValue ? [minValue] : []}
                                options={catalogOptions}
                                placeholder="Min"
                                addLabel="Add Value"
                                inputClassName="h-9 text-xs"
                                ariaLabel={`${composed.label} min`}
                                onOpenChange={(isOpen) =>
                                  handleRangePickerOpenChange(composed.minKey, isOpen)
                                }
                                onChange={(values) => {
                                  const next = values.length ? values[values.length - 1] : ''
                                  setState((prev) => ({
                                    ...prev,
                                    fields: patchComposedRangeFields(prev.fields, composed, 'min', next)
                                  }))
                                }}
                                onCreateOption={async (value) => {
                                  await addLabel(composed.catalogKey!, value)
                                  setState((prev) => ({
                                    ...prev,
                                    fields: patchComposedRangeFields(prev.fields, composed, 'min', value)
                                  }))
                                }}
                              />
                              <div className="flex min-w-0 items-center gap-1">
                                <MultiSelect
                                  className="min-w-0 flex-1"
                                  maxSelections={1}
                                  dropdownPortal
                                  values={maxValue ? [maxValue] : []}
                                  options={catalogOptions}
                                  placeholder="Max (optional)"
                                  addLabel="Add Value"
                                  inputClassName="h-9 text-xs"
                                  ariaLabel={`${composed.label} max`}
                                  interactionLocked={minIsNa}
                                  lockedHint="Max follows Min when N/A is selected"
                                  onOpenChange={(isOpen) =>
                                    handleRangePickerOpenChange(composed.minKey, isOpen)
                                  }
                                  onChange={(values) => {
                                    const next = values.length ? values[values.length - 1] : ''
                                    setState((prev) => ({
                                      ...prev,
                                      fields: patchComposedRangeFields(prev.fields, composed, 'max', next)
                                    }))
                                  }}
                                  onCreateOption={async (value) => {
                                    await addLabel(composed.catalogKey!, value)
                                    setState((prev) => ({
                                      ...prev,
                                      fields: patchComposedRangeFields(prev.fields, composed, 'max', value)
                                    }))
                                  }}
                                />
                                <ComposedRangeMaxClearButton
                                  label={composed.label}
                                  visible={Boolean(maxValue.trim()) && !minIsNa}
                                  onClear={() =>
                                    setState((prev) => ({
                                      ...prev,
                                      fields: patchComposedRangeFields(prev.fields, composed, 'max', '')
                                    }))
                                  }
                                />
                              </div>
                              {composed.unitKey && composed.unitOptions ? (
                                <InlineSelect
                                  value={
                                    state.units[composed.unitKey] || composed.unitOptions[0].value
                                  }
                                  options={composed.unitOptions}
                                  onChange={(value) =>
                                    setState((prev) => ({
                                      ...prev,
                                      units: { ...prev.units, [composed.unitKey!]: value }
                                    }))
                                  }
                                  className="w-full shrink-0 sm:w-20"
                                  buttonClassName="h-9 px-2 text-xs justify-between"
                                />
                              ) : null}
                            </div>
                          )
                        })()
                      ) : (
                      <div className="flex w-full flex-wrap items-center gap-3">
                        <div className="flex min-w-0 flex-1 items-center rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                          <input
                            type={composed.inputType === 'number' ? 'number' : 'text'}
                            step={composed.inputType === 'number' ? 'any' : undefined}
                            className="w-full min-w-0 border-0 bg-transparent px-3 py-2 text-xs focus:outline-none"
                            placeholder="Min"
                            aria-label={`${composed.label} min`}
                            value={state.fields[composed.minKey] || ''}
                            onChange={(event) =>
                              setState((prev) => ({
                                ...prev,
                                fields: { ...prev.fields, [composed.minKey]: event.target.value }
                              }))
                            }
                          />
                        </div>
                        <div className="flex min-w-0 flex-1 items-center gap-1">
                          <div className="flex min-w-0 flex-1 items-center rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                            <input
                              type={composed.inputType === 'number' ? 'number' : 'text'}
                              step={composed.inputType === 'number' ? 'any' : undefined}
                              className="w-full min-w-0 border-0 bg-transparent px-3 py-2 text-xs focus:outline-none"
                              placeholder="Max (optional)"
                              aria-label={`${composed.label} max`}
                              value={state.fields[composed.maxKey] || ''}
                              onChange={(event) =>
                                setState((prev) => ({
                                  ...prev,
                                  fields: { ...prev.fields, [composed.maxKey]: event.target.value }
                                }))
                              }
                            />
                          </div>
                          <ComposedRangeMaxClearButton
                            label={composed.label}
                            visible={Boolean(String(state.fields[composed.maxKey] ?? '').trim())}
                            onClear={() =>
                              setState((prev) => ({
                                ...prev,
                                fields: { ...prev.fields, [composed.maxKey]: '' }
                              }))
                            }
                          />
                        </div>
                        {composed.unitKey && composed.unitOptions ? (
                          <InlineSelect
                            value={state.units[composed.unitKey] || composed.unitOptions[0].value}
                            options={composed.unitOptions}
                            onChange={(value) =>
                              setState((prev) => ({
                                ...prev,
                                units: { ...prev.units, [composed.unitKey!]: value }
                              }))
                            }
                            className="w-20 shrink-0"
                            buttonClassName="h-9 px-2 text-xs justify-between"
                          />
                        ) : null}
                      </div>
                      )
                    ) : field.key === 'gasNozzleOrifice' ? (
                      <div className="flex w-full items-center rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                        <input
                          type="number"
                          step="any"
                          className="w-full border-0 bg-transparent px-3 py-2 text-xs focus:outline-none"
                          placeholder="0.00"
                          value={state.fields[field.key] || ''}
                          onChange={(event) =>
                            setState((prev) => ({
                              ...prev,
                              fields: { ...prev.fields, [field.key]: event.target.value }
                            }))
                          }
                        />
                        <InlineSelect
                          value={state.units[field.key] || 'in'}
                          options={[
                            { value: 'in', label: 'in' },
                            { value: 'mm', label: 'mm' }
                          ]}
                          onChange={(value) =>
                            setState((prev) => ({
                              ...prev,
                              units: { ...prev.units, [field.key]: value }
                            }))
                          }
                          className="w-16"
                          buttonClassName="h-9 px-2 text-xs justify-between bg-transparent"
                        />
                      </div>
                    ) : WELD_FIELD_CATALOG[field.key as keyof typeof WELD_FIELD_CATALOG] ? (
                      <>
                        <MultiSelect
                          className="flex-1"
                          values={state.fields[field.key] ? [state.fields[field.key]] : []}
                          options={getLabelsMerged(
                            WELD_FIELD_CATALOG[field.key as keyof typeof WELD_FIELD_CATALOG],
                            fieldOptions[field.key] ?? []
                          )}
                          placeholder="Select or add..."
                          addLabel="Add Value"
                          inputClassName="h-9 text-xs"
                          onChange={(values) => {
                            const next = values.length ? values[values.length - 1] : ''
                            setState((prev) => ({
                              ...prev,
                              fields: { ...prev.fields, [field.key]: next }
                            }))
                          }}
                          onCreateOption={async (value) => {
                            await addLabel(WELD_FIELD_CATALOG[field.key as keyof typeof WELD_FIELD_CATALOG], value)
                            setState((prev) => ({
                              ...prev,
                              fields: { ...prev.fields, [field.key]: value }
                            }))
                          }}
                        />
                        {field.unitType === 'length' &&
                          field.key !== 'ctwd' &&
                          field.key !== 'wireDiameter' && (
                          <InlineSelect
                            value={state.units[field.key] || 'in'}
                            options={[
                              { value: 'in', label: 'in' },
                              { value: 'mm', label: 'mm' }
                            ]}
                            onChange={(value) =>
                              setState((prev) => ({
                                ...prev,
                                units: { ...prev.units, [field.key]: value }
                              }))
                            }
                            className="w-16"
                            buttonClassName="h-9 px-2 text-xs justify-between"
                          />
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
                )
              })}
            </div>
          </div>
          )
        })}
      </div>
    </div>
  )
}
